import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { useGetPaging } from 'js/hooks/useGetPaging';
import { FORM_TYPE_CREATE_ELT } from 'js/constants/formTypes';
import { filter, findIndex, omit, each, has, isEmpty, isPlainObject, map } from 'lodash';
import UIUtils from 'js/lib/UIUtils';
import { updateFormData } from 'js/actions';
import React, { useMemo } from 'react';
import { ResourceParamField } from 'new/components/flows/configure/ELT/ELTSourceSetup/components/ResourceParamField';
import classes from 'new/components/flows/configure/ELT/ELTSourceSetup/SourceSetupPage.module.scss';
import { VendorFeedSpecs } from 'new/components/flows/types/elt/CreateELTFlow/types';
import { createVendorFeedSpecsWrapper } from '../utils/vendorFeedSpecsUtils';
import { CapabilityChecker } from '../constants/capabilities';

interface ResourceParamFieldsProps {
  selectedConnection: string;
  sourceConfig: any;
  formType: string;
  vendorFeedSpecs?: VendorFeedSpecs;
  templateVersionInfo?: {
    templateVersion: 'v1' | 'v2';
    isV2Template: boolean;
  };
  flowType?: string;
}

// V2 templates pass raw vendor resource_param objects straight through to
// FieldBuilder, while V1 goes through the paging reducer's normalization
// (getPagingParamFromVendorParam) which spreads `config` to top level and maps
// `allowed_values` -> `values`. Apply the equivalent transform here so V2
// fields render the right control, mirroring the canonical formatInputFields
// pattern in src/js/reducers/paging/index.js (lines ~619-635):
//   - lift config keys to top level (default, isHiddenField, etc.)
//   - map allowed_values -> values
//   - build options from values, wrapping primitives as {label, value}
//   - infer inputType: select-creatable when can_add_values=true, else select,
//     unless the field already specifies its own inputType.
// Written to be idempotent so passing already-normalized V1 fields is safe.
const normalizeResourceParam = (field: any) => {
  if (!field) return field;
  const config = field.config || {};
  const normalized: any = {
    ...config,
    ...field,
  };
  // allowed_values is the source of truth when present. Use isEmpty rather
  // than `!values` because an existing `values: []` is truthy in JS and
  // would otherwise be left in place.
  if (!isEmpty(field.allowed_values)) {
    normalized.values = field.allowed_values;
  }
  if (!isEmpty(normalized.values)) {
    // Build options from values using the same wrapping rule as
    // formatInputFields: keep plain {label, value} objects as-is, otherwise
    // promote primitives. This is what FieldBuilder ultimately needs and
    // also overrides any stray empty `options: []` that might have come
    // from config — empty arrays are truthy and would shadow values via
    // `field.options || field.values?.map(...)` in FieldBuilder.
    normalized.options = map(normalized.values, (item: any) => {
      if (isPlainObject(item) && has(item, 'value') && item.label) {
        return item;
      }
      return { value: item, label: item };
    });
    if (!normalized.inputType) {
      normalized.inputType = normalized.can_add_values === true ? 'select-creatable' : 'select';
    }
  }
  const dataType = normalized.data_type || normalized.type;
  if (!normalized.inputType && (dataType === 'string_multi' || dataType === 'string-multi')) {
    normalized.inputType = 'textarea';
  }
  return normalized;
};

export const ResourceParamFields = ({
  selectedConnection,
  sourceConfig,
  formType,
  vendorFeedSpecs,
  templateVersionInfo,
  flowType = 'elt', // Default to 'elt' for backward compatibility
}: ResourceParamFieldsProps) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const { sourceConfigById } = useGetPaging();

  // Create wrapper for type-safe access to vendor feed specs
  const vendorFeedSpecsWrapper = useMemo(() => {
    return vendorFeedSpecs
      ? createVendorFeedSpecsWrapper(vendorFeedSpecs, templateVersionInfo?.templateVersion || 'v1')
      : null;
  }, [vendorFeedSpecs, templateVersionInfo?.templateVersion]);

  // Get fields from sourceConfigById (legacy) or from vendorFeedSpecs capabilities (v2)
  const fields = useMemo(() => {
    if (vendorFeedSpecsWrapper?.isV2Template()) {
      // For V2 templates, extract params from capabilities
      const capabilityParams: any[] = [];

      // Extract params from each enabled capability (only user-configurable ones)
      Object.entries(vendorFeedSpecs?.capabilities || {}).forEach(
        ([capabilityKey, capability]: [string, any]) => {
          if (
            capability.enabled &&
            capability.params &&
            CapabilityChecker.isUserConfigurable(capabilityKey as any)
          ) {
            capabilityParams.push(...capability.params);
          }
        },
      );

      // Also include additional_params (non-capability parameters)
      const additionalParams = vendorFeedSpecs?.additional_params || [];

      // Combine capability params and additional params
      return [...capabilityParams, ...additionalParams].map(normalizeResourceParam);
    } else {
      const v1Fields = filter(
        sourceConfigById[selectedConnection],
        ({ name, is_common_param = true }) => {
          // we want to only include params that are needed in the common payload section
          return name !== 'vendor_endpoint_id' && is_common_param;
        },
      );

      return v1Fields.map(normalizeResourceParam);
    }
  }, [vendorFeedSpecsWrapper, vendorFeedSpecs, sourceConfigById, selectedConnection]);

  // move start.cron field to the end
  const cronFieldIndex = findIndex(fields, { name: 'start.cron' });
  const cronField = fields[cronFieldIndex];

  // Remove cron field from the main fields array for separate handling
  const otherFields = fields.filter((field) => field.name !== 'start.cron');

  // Custom conditional display logic for ELT flows (works for all input types)
  const shouldRenderFieldForELT = (field, values) => {
    const { conditionalDisplay, displayOn, inputType } = field;

    // First check the standard shouldRenderField logic
    let shouldRender = UIUtils.shouldRenderField(field, values);

    // For ELT flows, also apply conditional display logic to non-button input types
    if (conditionalDisplay === true && shouldRender === true && inputType !== 'button') {
      each(displayOn, (critField, idx) => {
        if (has(values, critField.name) && values[critField.name] !== critField.value) {
          shouldRender = false;
          return false;
        }
      });
    }

    return shouldRender;
  };

  const onFieldUpdated = (name, value) => {
    let newData = { ...(sourceConfig || {}) };

    if (name) {
      newData[name] = value;
    }

    // Ensure ui.flow_type is set for conditional display logic
    if (!newData['ui.flow_type']) {
      newData['ui.flow_type'] = flowType;
    }

    //check if any conditional params are to be rendered. if they are missing in source config add their default value.
    newData = UIUtils.configWithMissingDefaults(newData, fields);
    // omit fields which shouldn't be rendered - use our custom logic for ELT flows
    const invisibleFieldNames = fields
      .filter((field) => !shouldRenderFieldForELT(field, newData))
      .map((field) => field.name);
    const visibleFieldData = omit(newData, invisibleFieldNames);

    dispatch(updateFormData(formType, { sourceConfig: visibleFieldData }));
  };

  return (
    <>
      {/* Other fields in the grid */}
      {otherFields.map((field, idx) => (
        <ResourceParamField
          key={field?.param_id || idx}
          field={field}
          onFieldUpdated={onFieldUpdated}
          sourceConfig={sourceConfig}
        />
      ))}
      {/* Full-width field for cron/fetch data - outside the grid */}
      {cronField && (
        <div className={classes.inputRowFullWidth}>
          <ResourceParamField
            key={cronField?.param_id || 'cron'}
            field={cronField}
            onFieldUpdated={onFieldUpdated}
            sourceConfig={sourceConfig}
          />
        </div>
      )}
    </>
  );
};
